/**
 * ============================================================================
 * DeviationTriage — Phase 7 frontend (Deviation Management Module).
 * Owner: Phase 7 frontend agent.
 * Route: /deviations (orchestrator wires this in router.tsx + AppShell.tsx).
 * Purpose: QA-Manager triage queue. Five hero KPI tiles up top
 *          (total / open / awaiting triage / critical / SLA breached),
 *          a classification + status + source-area filter bar, and the
 *          deviation list table below.
 *
 * URS rows: DEV-002 (triage queue), DEV-004 (classification filter),
 *           DEV-012 (Critical SLA visibility).
 *
 * A11Y: chips carry both a coloured dot and a textual label so colour is
 *       not the sole signal (WCAG 1.4.1).
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PageHero, Button } from '@edms/design-system';
import styles from './DeviationTriage.module.css';
import { NewDeviationDialog } from './NewDeviationDialog';
import { AiClusterInsight, AiResolutionTimeBadge } from '../../components/AiCluster';
import {
  useDeviationStatsQuery,
  useDeviationsQuery,
  type DeviationClassification,
  type DeviationListItem,
  type DeviationStatus,
  type DeviationsQueryParams,
  type SourceArea,
} from './useDeviationsQuery';

const CRIT_CLASS: Record<DeviationClassification, string> = {
  CRITICAL: styles.critCritical,
  MAJOR: styles.critMajor,
  MINOR: styles.critMinor,
};

const STATUS_CLASS: Record<DeviationStatus, string> = {
  REPORTED: styles.statReported,
  TRIAGED: styles.statTriaged,
  INVESTIGATION: styles.statInvestigation,
  RCA: styles.statRca,
  CLOSED: styles.statClosed,
  CANCELLED: styles.statCancelled,
};

const CLASSIFICATIONS: DeviationClassification[] = ['CRITICAL', 'MAJOR', 'MINOR'];
const STATUSES: DeviationStatus[] = [
  'REPORTED',
  'TRIAGED',
  'INVESTIGATION',
  'RCA',
  'CLOSED',
  'CANCELLED',
];
const SOURCE_AREAS: SourceArea[] = [
  'MANUFACTURING',
  'PACKAGING',
  'WAREHOUSE',
  'LAB',
  'UTILITIES',
  'QA',
  'IT',
  'OTHER',
];

export interface DeviationTriageProps {
  onOpenDeviation?: (id: string) => void;
}

export function DeviationTriage({ onOpenDeviation }: DeviationTriageProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DeviationsQueryParams>({});
  const [showNew, setShowNew] = useState(false);

  const listQ = useDeviationsQuery(filters);
  const statsQ = useDeviationStatsQuery();

  const items = listQ.data?.items ?? [];
  const stats = statsQ.data;

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }),
    [i18n.language],
  );
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  function openDeviation(d: DeviationListItem) {
    if (onOpenDeviation) {
      onOpenDeviation(d.deviationId);
      return;
    }
    navigate({ to: '/deviations/$id', params: { id: d.deviationId } });
  }

  const isEmpty = !listQ.isLoading && !listQ.isError && items.length === 0;

  return (
    <div className={styles.screen}>
      <PageHero
        eyebrow={t('deviation.triage.eyebrow', { defaultValue: 'Quality Events' })}
        title={t('deviation.triage.title', {
          defaultValue: 'Deviation triage queue',
        })}
        subtitle={t('deviation.triage.subtitle', {
          defaultValue:
            'Quality-Manager view of reported deviations awaiting triage, investigation, and RCA closure. Critical-tier rows are highlighted.',
        })}
        actions={
          <Button variant="primary" onClick={() => setShowNew(true)}>
            {t('deviation.triage.reportCta', { defaultValue: 'Report deviation' })}
          </Button>
        }
      />

      <section
        className={styles.statsRow}
        aria-label={t('deviation.triage.stats.aria', {
          defaultValue: 'Triage queue stats',
        })}
      >
        <div className={styles.statTile}>
          <span className={styles.statTileLabel}>
            {t('deviation.triage.stats.total', { defaultValue: 'All deviations' })}
          </span>
          <span className={styles.statTileValue}>
            {stats ? nf.format(stats.total) : '—'}
          </span>
        </div>
        <div className={styles.statTile}>
          <span className={styles.statTileLabel}>
            {t('deviation.triage.stats.open', { defaultValue: 'Open' })}
          </span>
          <span className={styles.statTileValue}>
            {stats ? nf.format(stats.open) : '—'}
          </span>
        </div>
        <div className={styles.statTile}>
          <span className={styles.statTileLabel}>
            {t('deviation.triage.stats.awaitingTriage', {
              defaultValue: 'Awaiting triage',
            })}
          </span>
          <span
            className={`${styles.statTileValue} ${stats && stats.awaitingTriage > 0 ? styles.statTileValueWarn : ''}`}
          >
            {stats ? nf.format(stats.awaitingTriage) : '—'}
          </span>
        </div>
        <div className={styles.statTile}>
          <span className={styles.statTileLabel}>
            {t('deviation.triage.stats.critical', { defaultValue: 'Critical-tier' })}
          </span>
          <span
            className={`${styles.statTileValue} ${stats && stats.critical > 0 ? styles.statTileValueDanger : ''}`}
          >
            {stats ? nf.format(stats.critical) : '—'}
          </span>
        </div>
        <div className={styles.statTile}>
          <span className={styles.statTileLabel}>
            {t('deviation.triage.stats.slaBreached', { defaultValue: 'SLA breached' })}
          </span>
          <span
            className={`${styles.statTileValue} ${stats && stats.slaBreached > 0 ? styles.statTileValueDanger : ''}`}
          >
            {stats ? nf.format(stats.slaBreached) : '—'}
          </span>
        </div>
      </section>

      <section className={styles.card} aria-label={t('deviation.triage.list.aria', { defaultValue: 'Deviation list' })}>
        <div className={styles.filterBar}>
          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="filter-classification">
              {t('deviation.filter.classification', { defaultValue: 'Classification' })}
            </label>
            <select
              id="filter-classification"
              className={styles.filterSelect}
              value={filters.classification ?? ''}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  classification:
                    e.target.value === '' ? undefined : (e.target.value as DeviationClassification),
                }))
              }
            >
              <option value="">
                {t('common.any', { defaultValue: 'Any' })}
              </option>
              {CLASSIFICATIONS.map((c) => (
                <option key={c} value={c}>
                  {t(`deviation.classification.${c}`, { defaultValue: c })}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="filter-status">
              {t('deviation.filter.status', { defaultValue: 'Status' })}
            </label>
            <select
              id="filter-status"
              className={styles.filterSelect}
              value={filters.status ?? ''}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  status: e.target.value === '' ? undefined : (e.target.value as DeviationStatus),
                }))
              }
            >
              <option value="">{t('common.any', { defaultValue: 'Any' })}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`deviation.status.${s}`, { defaultValue: s })}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="filter-source-area">
              {t('deviation.filter.sourceArea', { defaultValue: 'Source area' })}
            </label>
            <select
              id="filter-source-area"
              className={styles.filterSelect}
              value={filters.sourceArea ?? ''}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  sourceArea: e.target.value === '' ? undefined : (e.target.value as SourceArea),
                }))
              }
            >
              <option value="">{t('common.any', { defaultValue: 'Any' })}</option>
              {SOURCE_AREAS.map((a) => (
                <option key={a} value={a}>
                  {t(`deviation.sourceArea.${a}`, { defaultValue: a })}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className={styles.filterClear}
            onClick={() => setFilters({})}
            aria-label={t('common.clearFilters', { defaultValue: 'Clear filters' })}
          >
            {t('common.clear', { defaultValue: 'Clear' })}
          </button>
        </div>

        {/* AI-201 — root-cause clustering. Renders only when there's enough to cluster. */}
        {items.length >= 2 && (
          <div style={{ margin: '12px 0' }}>
            <AiClusterInsight
              entityKind="deviation"
              title={t('deviation.triage.aiClusters', { defaultValue: 'AI sees patterns across these deviations' })}
              minSimilarity={0.55}
              items={items.map((d) => ({
                id: d.deviationId,
                text: `${d.title} (${d.classification} · ${d.sourceArea ?? 'no-area'})`,
                // Show the human-readable deviation number, not the UUID, and
                // link via a real (path-based) route, not a dead hash.
                label: `${d.deviationNumber} — ${d.title}`,
                href: `/deviations/${encodeURIComponent(d.deviationId)}`,
              }))}
            />
          </div>
        )}

        {listQ.isLoading && (
          <div role="status" aria-live="polite">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className={styles.skeletonRow} aria-hidden="true" />
            ))}
          </div>
        )}

        {listQ.isError && (
          <div className={styles.errorBanner} role="alert">
            {t('deviation.triage.errors.loadFailed', {
              defaultValue: 'Could not load the deviation queue. Retry?',
            })}
            <button
              type="button"
              className={styles.filterClear}
              style={{ marginInlineStart: 'var(--space-3)' }}
              onClick={() => listQ.refetch()}
            >
              {t('common.retry', { defaultValue: 'Retry' })}
            </button>
          </div>
        )}

        {isEmpty && (
          <div className={styles.emptyState}>
            <h3 style={{ margin: 0 }}>
              {t('deviation.triage.empty.title', {
                defaultValue: 'No deviations match your filters.',
              })}
            </h3>
            <p style={{ margin: 0 }}>
              {t('deviation.triage.empty.body', {
                defaultValue:
                  'Clear filters above to see the full queue. Use the report flow to file a new deviation.',
              })}
            </p>
          </div>
        )}

        {!listQ.isLoading && !listQ.isError && items.length > 0 && (
          <div className={styles.tableWrap}>
            <table
              className={styles.table}
              aria-label={t('deviation.triage.tableAria', {
                defaultValue: 'Deviations',
              })}
            >
              <caption className={styles.srOnly}>
                {t('deviation.triage.tableCaption', {
                  defaultValue:
                    'Deviation queue with number, title, classification, source area, status, SLA, and quick links to the detail view.',
                })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">
                    {t('deviation.cols.number', { defaultValue: 'Number' })}
                  </th>
                  <th scope="col">
                    {t('deviation.cols.title', { defaultValue: 'Title' })}
                  </th>
                  <th scope="col">
                    {t('deviation.cols.classification', { defaultValue: 'Classification' })}
                  </th>
                  <th scope="col">
                    {t('deviation.cols.sourceArea', { defaultValue: 'Source area' })}
                  </th>
                  <th scope="col">
                    {t('deviation.cols.status', { defaultValue: 'Status' })}
                  </th>
                  <th scope="col">
                    {t('deviation.cols.sla', { defaultValue: 'SLA due' })}
                  </th>
                  <th scope="col">
                    {t('deviation.cols.requiresCapa', { defaultValue: 'CAPA' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.deviationId}>
                    <td>
                      <button
                        type="button"
                        className={styles.linkBtn}
                        onClick={() => openDeviation(d)}
                        aria-label={t('deviation.triage.openAria', {
                          defaultValue: 'Open deviation {{n}}',
                          n: d.deviationNumber,
                        })}
                      >
                        {d.deviationNumber}
                      </button>
                    </td>
                    <td>{d.title}</td>
                    <td>
                      <span
                        className={`${styles.chip} ${CRIT_CLASS[d.classification]}`}
                        aria-label={t('deviation.classification.aria', {
                          defaultValue: 'Classification: {{level}}',
                          level: t(`deviation.classification.${d.classification}`, {
                            defaultValue: d.classification,
                          }),
                        })}
                      >
                        <span
                          aria-hidden="true"
                          className={`${styles.dot} ${d.classification === 'CRITICAL' ? styles.dotCritical : d.classification === 'MAJOR' ? styles.dotWarn : styles.dotInfo}`}
                        />
                        {t(`deviation.classification.${d.classification}`, {
                          defaultValue: d.classification,
                        })}
                      </span>
                    </td>
                    <td>
                      {t(`deviation.sourceArea.${d.sourceArea}`, {
                        defaultValue: d.sourceArea,
                      })}
                    </td>
                    <td>
                      <span className={`${styles.chip} ${STATUS_CLASS[d.status]}`}>
                        {t(`deviation.status.${d.status}`, { defaultValue: d.status })}
                      </span>
                    </td>
                    <td>
                      {d.slaDueUtc ? (
                        <>
                          <span>{dateFmt.format(new Date(d.slaDueUtc))}</span>
                          {d.slaBreached && (
                            <>
                              {' '}
                              <span className={styles.slaBreachBadge}>
                                {t('deviation.sla.breached', { defaultValue: 'BREACHED' })}
                              </span>
                            </>
                          )}
                        </>
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                      {/* AI-203 — resolution-time prediction badge */}
                      {d.status !== 'CLOSED' && d.status !== 'CANCELLED' && (
                        <>
                          {' '}
                          <AiResolutionTimeBadge
                            recordKind="deviation"
                            recordId={d.deviationId}
                            context={`${d.classification} ${d.title} in ${d.sourceArea ?? 'unknown'} area`}
                            slaDays={d.classification === 'CRITICAL' ? 7 : d.classification === 'MAJOR' ? 14 : 30}
                          />
                        </>
                      )}
                    </td>
                    <td>
                      {d.requiresCapa
                        ? t('common.yes', { defaultValue: 'Yes' })
                        : t('common.no', { defaultValue: 'No' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showNew && (
        <NewDeviationDialog
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            void listQ.refetch();
            void statsQ.refetch();
          }}
        />
      )}
    </div>
  );
}
